<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { Head, Link, useForm, usePage } from '@inertiajs/vue3';
import { ChevronDownIcon, ArrowUturnLeftIcon } from '@heroicons/vue/20/solid';
import { AdjustmentsHorizontalIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import {
    Dialog,
    DialogOverlay,
    DialogPanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import Filters from './Partials/Filters.vue';
import Pagination from '@/Pages/Event/Partials/Pagination.vue';
import { useTitle as title } from '@/Composables/title.js';
import SearchAndResetButtons from '@/Pages/CorporatePublishing/Partials/SearchAndResetButtons.vue';
import Breadcrumb from '@/Components/Breadcrumb.vue';
import { useArticleComponentMap } from '@/Composables/articleComponentMap.js';
import { useI18n } from '@/Composables/i18n.js';

const { getTranslationsByPrefix } = useI18n();

const page = usePage();

const props = defineProps({
    articles: {
        type: Array,
        required: true,
    },
    linkCollection: {
        type: Array,
        required: true,
    },
    total: {
        type: Number,
        default: null,
    },
    businessAreas: {
        type: Array,
        required: true,
    },
    topics: {
        type: Array,
        required: true,
    },
    collectionTypes: {
        type: Array,
        required: true,
    },
    formState: {
        type: Object,
        default: null,
    },
});

const filterMenuIsOpen = ref(false);

const initialDateValues = null;

/* eslint-disable camelcase */
const initialFormData = {
    query: '',
    dates: initialDateValues,
    topics: [],
    business_areas: [],
    format_categories: [],
    collection_types: [],
    order_by: 'date',
    order_direction: 'asc',
};

const form = useForm(initialFormData);

onBeforeMount(() => {
    for (const [key, value] of Object.entries(props.formState)) {
        form[key] = value;
    }

    form.defaults();
});

const setOrder = (by, direction) => {
    form.order_by = by;
    form.order_direction = direction;

    submit();
};

const orderItems = Object.entries(getTranslationsByPrefix('articles.search.sorting')).map(translation => {
    const [by, direction] = translation[0].split('_');

    return { by, direction, translation: translation[1] };
});

const formCanBeSent = computed(() => {
    if (form.query && form.query.length > 0) {
        return form.isDirty && form.query.length > 2;
    }

    return form.isDirty;
});

const formCanBeReset = computed(() => {
    const { order_by, order_direction, ...initialFormState } = props.formState;

    return formCanBeSent.value || Object.keys(initialFormState).length > 0;
});
/* eslint-enable camelcase */

const submit = () => form.post(route('articles.search.index'), {
    preserveScroll: true,
    preserveState: false,
});
</script>

<template>
    <Head>
        <title>{{ title($t('articles.page.title')) }}</title>
        <meta
            name="robots"
            content="noindex,follow"
        >
    </Head>

    <div class="relative bg-white">
        <div class="container px-4 md:py-4 mx-auto space-y-6">
            <div>
                <Breadcrumb
                    class="mt-4"
                />

                <h1 class="font-semibold text-3xl sm:text-4xl text-deep-teal-700 mt-4">
                    {{ $t('articles.page.title') }}
                </h1>
            </div>

            <!-- search form -->
            <form
                id="search-form"
                class="bg-gray-50 p-4 sm:p-6 space-y-6 -mx-4 sm:mx-0"
                @submit.prevent="submit"
            >
                <div class="flex space-x-4">
                    <div class="flex rounded-md shadow-sm ring-1 ring-gray-300 grow group">
                        <input
                            id="query"
                            v-model="form.query"
                            :placeholder="$t('articles.search.query.placeholder')"
                            class="block w-full text-gray-900 leading-relaxed py-2 border-0 rounded-md placeholder:text-gray-500 focus:ring-2 focus:ring-deep-teal-600"
                            maxlength="50"
                            minlength="3"
                            name="query"
                            type="search"
                        >
                    </div>

                    <button
                        class="inline-flex lg:hidden items-center justify-center text-deep-teal-700 focus:outline-none focus:text-deep-teal-900"
                        type="button"
                        @click="filterMenuIsOpen = true"
                    >
                        <span class="sr-only">{{ $t('articles.search.openFilters') }}</span>
                        <AdjustmentsHorizontalIcon
                            aria-hidden="true"
                            class="w-6 h-6"
                        />
                    </button>
                </div>

                <!-- filters -->
                <div class="flex gap-y-4 lg:gap-y-0 lg:gap-x-6">
                    <div class="hidden lg:block grow">
                        <Filters
                            v-model:form-business-areas="form.business_areas"
                            v-model:form-dates="form.dates"
                            v-model:form-topics="form.topics"
                            v-model:form-collection-types="form.collection_types"
                            :business-areas
                            :topics
                            :collection-types
                            :form-can-be-reset
                            :form-can-be-sent
                            :form-is-processing="form.processing"
                            @update:business-areas="value => form.business_areas = value"
                            @update:topics="value => form.topics = value"
                            @update:collection-types="value => form.collection_types = value"
                            @update:dates="value => form.dates = value"
                        />
                    </div>

                    <SearchAndResetButtons
                        class=""
                        :disabled-reset="!formCanBeReset"
                        :disabled-search="!formCanBeSent || form.processing"
                    />
                </div>

                <!-- mobile filter menu -->
                <TransitionRoot
                    :show="filterMenuIsOpen"
                    appear
                    as="template"
                >
                    <Dialog
                        :unmount="false"
                        as="div"
                        class="fixed inset-0 flex z-40 w-full lg:hidden"
                        @close="filterMenuIsOpen = false"
                    >
                        <TransitionChild
                            as="template"
                            enter="transition-opacity ease-linear duration-300"
                            enter-from="opacity-0"
                            enter-to="opacity-100"
                            leave="transition-opacity ease-linear duration-300"
                            leave-from="opacity-100"
                            leave-to="opacity-0"
                        >
                            <DialogOverlay class="fixed inset-0 bg-black bg-opacity-25" />
                        </TransitionChild>

                        <TransitionChild
                            as="template"
                            enter="transition ease-in-out duration-300 transform"
                            enter-from="translate-x-full"
                            enter-to="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leave-from="translate-x-0"
                            leave-to="translate-x-full"
                        >
                            <div class="fixed inset-y-0 left-auto right-0">
                                <DialogPanel class="h-full w-80 flex flex-col bg-white shadow-xl overflow-y-auto">
                                    <div
                                        class="bg-deep-teal-700 flex items-center justify-between font-semibold text-base text-white px-4 py-5"
                                    >
                                        <h3>
                                            {{ $t('articles.search.filters.title') }}
                                        </h3>

                                        <!-- close button-->
                                        <button
                                            class="inline-flex items-center justify-center"
                                            type="button"
                                            @click="filterMenuIsOpen = false"
                                        >
                                            <span class="sr-only">{{ $t('articles.search.filters.closeMenu') }}</span>
                                            <XMarkIcon
                                                aria-hidden="true"
                                                class="w-6 h-6"
                                            />
                                        </button>
                                    </div>

                                    <!-- menu items -->
                                    <div class="h-full flex flex-col justify-between p-4 space-y-8 overflow-y-auto">
                                        <Filters
                                            v-model:form-business-areas="form.business_areas"
                                            v-model:form-dates="form.dates"
                                            v-model:form-topics="form.topics"
                                            v-model:form-collection-types="form.collection_types"
                                            :business-areas
                                            :topics
                                            :collection-types
                                            :form-can-be-reset
                                            :form-can-be-sent
                                            :form-is-processing="form.processing"
                                            @update:business-areas="value => form.business_areas = value"
                                            @update:topics="value => form.topics = value"
                                            @update:collection-types="value => form.collection_types = value"
                                            @update:dates="value => form.dates = value"
                                        />

                                        <button
                                            :disabled="!form.isDirty || form.processing"
                                            class="w-full px-3 py-2 text-base font-bold bg-deep-teal-700 text-white rounded-3xl disabled:bg-deep-teal-300 focus:ring-1 focus:ring-deep-teal-600 focus:outline-none"
                                            form="search-form"
                                            type="submit"
                                        >
                                            {{ $t('articles.search.submit') }}
                                        </button>
                                    </div>
                                </DialogPanel>
                            </div>
                        </TransitionChild>
                    </Dialog>
                </TransitionRoot>
            </form>

            <div class="flex justify-between border-b border-b-gray-200 pb-2 sm:pb-4">
                <!-- total item counter-->
                <p class="font-semibold text-gray-500">
                    {{ $tChoice('articles.results.count', total) }}
                </p>

                <!-- sort menu -->
                <Menu
                    as="div"
                    class="relative inline-block text-left"
                >
                    <div>
                        <MenuButton
                            class="inline-flex items-center font-semibold text-base text-deep-teal-700 hover:text-deep-teal-900 focus:outline-none focus:underline"
                            :class="{ 'text-gray-400 hover:text-gray-400 pointer-events-none': !total }"
                            :disabled="!total"
                        >
                            {{
                                orderItems.find(
                                    (i) => i.by === form.order_by && i.direction === form.order_direction)
                                    ?.translation
                            }}

                            <ChevronDownIcon
                                aria-hidden="true"
                                class="h-5 w-5 ml-1.5"
                            />
                        </MenuButton>
                    </div>

                    <transition
                        enter-active-class="transition duration-100 ease-out"
                        enter-from-class="transform scale-95 opacity-0"
                        enter-to-class="transform scale-100 opacity-100"
                        leave-active-class="transition duration-75 ease-in"
                        leave-from-class="transform scale-100 opacity-100"
                        leave-to-class="transform scale-95 opacity-0"
                    >
                        <MenuItems
                            class="absolute mt-1.5 max-h-60 w-full overflow-auto rounded-md bg-white text-base shadow-lg ring-1 ring-gray-500 ring-opacity-5 focus:outline-none sm:text-sm"
                        >
                            <MenuItem
                                v-for="(item, index) in orderItems"
                                :key="index"
                                :class="{ 'bg-gray-100 font-bold': form.order_by === item.by && form.order_direction === item.direction }"
                                as="button"
                                class="block w-full hover:bg-gray-100 text-left text-gray-800 hover:text-gray-900 relative cursor-default select-none py-2 pl-3"
                                @click="setOrder(item.by, item.direction)"
                            >
                                {{ item.translation }}
                            </MenuItem>
                        </MenuItems>
                    </transition>
                </Menu>
            </div>

            <template v-if="!total">
                <p>
                    {{ $t('articles.results.notFound') }}.
                </p>

                <Link
                    class="flex text-deep-teal-700 disabled:text-gray-400 hover:underline disabled:no-underline hover:text-deep-teal-900 focus:underline focus:text-deep-teal-900 focus:outline-none"
                    :href="route('articles.index')"
                    preserve-scroll
                >
                    <ArrowUturnLeftIcon
                        class="shrink-0 h-6 w-6 mr-2 opacity-75"
                        aria-hidden="true"
                    />
                    {{ $t('articles.search.reset') }}
                </Link>
            </template>
        </div>

        <!-- article list -->
        <div
            v-if="articles.length > 0"
            class="container mx-auto px-4 py-8"
        >
            <ul
                role="list"
                class="grid grid-cols-1 lg:grid-cols-12 gap-8"
            >
                <li
                    v-for="(article, index) in articles"
                    :key="index"
                    class="lg:col-start-2 lg:col-span-10"
                >
                    <component
                        :is="useArticleComponentMap(article.collectionType)"
                        :title="article.title"
                        :teaser="article.teaser"
                        :url="article.url"
                        :business-areas="article.event_business_areas"
                        :topics="article.event_topics"
                        :advertorial="article.advertorial"
                        :type="article.collectionType === 'publications' ? article.category : article.collectionType"
                        :date="article.published_at"
                        :contributors="article.contributor_profiles?.map(profile => ({ name: profile.fullname }))"
                        :image="{ ...article.images?.card, alt: article.images?.alt }"
                        :is-external="article.site !== page.props.siteName || article.collectionType === 'publications'"
                        :is-publication="article.collectionType === 'publications'"
                    />
                </li>
            </ul>
        </div>

        <!-- pagination -->
        <div class="container px-4 mx-auto my-8">
            <Pagination
                v-if="linkCollection && Object.keys(linkCollection).length > 0"
                :link-collection
            />
        </div>
    </div>
</template>
